// pages/ProductDeliverySettings.js
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import ProductNavbar from "./ProductNavbar";
import axios from "axios";
import { baseurl } from "../../baseurl";
import Swal from "sweetalert2";

function ProductDelivery() {
  const { uuid } = useParams();
  const [deliveryTimes, setDeliveryTimes] = useState([
    { time: "" } // Initial empty state
  ]);
  const [loading, setLoading] = useState(false);
  const [productName, setProductName] = useState("");

  // 1. Fetch existing settings on component load
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${baseurl}getProductDeliverySettings/${uuid}`);
        
        // Match the specific response structure: response.data.data.delivery_times
        if (response.data.success && response.data.data) {
          const fetchedTimes = response.data.data.delivery_times;
          setProductName(response.data.data.product_name);

          if (Array.isArray(fetchedTimes) && fetchedTimes.length > 0) {
            setDeliveryTimes(fetchedTimes);
          }
        }
      } catch (error) {
        console.error("Error fetching delivery settings:", error);
      }
    };
    if (uuid) fetchSettings();
  }, [uuid]);

  const handleTimeChange = (index, value) => {
    const newTimes = [...deliveryTimes];
    newTimes[index].time = value;
    setDeliveryTimes(newTimes);
  };

  const addTimeOption = () => {
    setDeliveryTimes([...deliveryTimes, { time: "" }]);
  };

  const removeTimeOption = (index) => {
    const newTimes = deliveryTimes.filter((_, i) => i !== index);
    setDeliveryTimes(newTimes.length ? newTimes : [{ time: "" }]);
  };

  // 2. Handle Save to Backend
  const handleSave = async () => {
    const filteredTimes = deliveryTimes.filter(item => item.time.trim() !== "");

    if (filteredTimes.length === 0) {
      Swal.fire("Validation Error", "Please provide at least one delivery time option.", "warning");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        product_uuid: uuid,
        delivery_times: filteredTimes
      };

      const response = await axios.post(`${baseurl}updateProductDeliverySettings`, payload);

      if (response.data.success) {
        Swal.fire("Success", "Delivery settings updated successfully!", "success");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      Swal.fire("Error", "Failed to save settings.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper">
        <div className="main-content">
          <ProductNavbar />

          <div className="page-breadcrumb d-none d-sm-flex align-items-center mb-3">
            <div className="breadcrumb-title pe-3">Product Delivery Settings</div>
            {productName && (
               <div className="ps-3 text-muted border-start ms-3">
                 Product: <strong>{productName}</strong>
               </div>
            )}
          </div>

          <div className="col-12 col-lg-8 mx-auto mb-4">
            <div className="card border-top border-0 border-4 border-primary shadow-sm">
              <div className="card-header py-3">
                <h5 className="mb-0 text-primary">Configure Delivery Time</h5>
                <small className="text-muted">UUID: {uuid}</small>
              </div>
              <div className="card-body">
                
                {/* Vendor Notification Note */}
                <div className="alert alert-info border-0 bg-light-info py-3 mb-4">
                  <div className="d-flex align-items-center">
                    <div className="fs-3 text-info">
                      <i className="bi bi-info-circle-fill"></i>
                    </div>
                    <div className="ms-3">
                      <div className="text-info"><strong>Important Pricing Note:</strong></div>
                      <div className="text-dark">
                        Delivery charges must be included in the product price. 
                        <strong> Customers will see these as "Free Delivery" options.</strong>
                      </div>
                    </div>
                  </div>
                </div>

                <label className="form-label fw-bold mt-2">Estimated Delivery Durations</label>
                
                {deliveryTimes.map((item, index) => (
                  <div className="row mb-3 align-items-center" key={index}>
                    <div className="col-9">
                      <div className="input-group">
                        <span className="input-group-text bg-light">
                          <i className="bi bi-clock"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="e.g., 3-5 Business Days"
                          value={item.time}
                          onChange={(e) => handleTimeChange(index, e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="col-3">
                      <button
                        type="button"
                        className="btn btn-outline-danger w-100"
                        onClick={() => removeTimeOption(index)}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}

                <button 
                  type="button" 
                  className="btn btn-sm btn-outline-primary mb-4" 
                  onClick={addTimeOption}
                >
                  ➕ Add Delivery Time Option
                </button>

                <hr />

                <div className="col-12 text-end">
                  <button 
                    type="button" 
                    className="btn btn-success px-5" 
                    onClick={handleSave}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      "💾 Save Delivery Settings"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default ProductDelivery;